import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MobileSidebar } from './MobileSidebar';
import { Header as BaseHeader } from './Header';
import { getTestUserProvider, getEnhancedComponents } from '../../utils/componentFactory';
import { verifyAuth } from '../../utils/authHelper';
import axios from 'axios';
import PaymentModal from '../common/PaymentModal';
import PricingPage from '../../features/pricing/PricingPage';
import Chat from '../../features/chat/Chat';
import ArtifactPanel from '../../features/chat/components/ArtifactPanel';
import { MessageStatusProvider } from '../../features/chat/hooks/useMessageStatus';
import Profile from '../../features/profile/Profile';
import Dashboard from '../../features/dashboard/Dashboard';
import Documents from '../../features/dashboard/Documents';
import Alerts from '../../features/dashboard/Alerts';
import ProjectDashboard from '../../features/project/ProjectDashboard';
import ClientView from '../../features/client/ClientView';
import InvitationAcceptedNotification from '../notifications/InvitationAcceptedNotification';
import NotificationsPanel from '../notifications/NotificationsPanel';
import websocketService from '../../utils/websocketService';
import { getCachedChatTitle, cacheChatTitle } from '../../utils/chatTitleCache';
import TwoFactorEnforcementModal from '../TwoFactorEnforcementModal';
import AccountModal from './AccountModal';
import { useTheme } from '../../features/theme/context/ThemeContext';
import { splitProviderModelKey } from '../../utils/defaultChatModel';

const CHAT_LIST_CACHE_KEY = 'chatListCache';
const CHAT_LIST_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getInitialChatTitle = () => {
  if (typeof window === 'undefined') return null;
  try {
    const match = window.location.pathname.match(/\/chat\/([^/]+)/);
    const initialChatId = match ? match[1] : localStorage.getItem('lastChatId');
    if (!initialChatId) return null;
    return getCachedChatTitle(initialChatId);
  } catch (error) {
    console.warn('Error determining initial chat title', error);
    return null;
  }
};

// Get environment-appropriate components
const TestUserProvider = getTestUserProvider();
const enhancedComponents = getEnhancedComponents({
  Header: BaseHeader
});
const { Header } = enhancedComponents;

const MainInterface = ({ user, refreshUser, setIsAuthenticated, setUser, setIsOnboarded, isLoading, isAuthenticated }) => {
  const [activeChat, setActiveChat] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentView, setPaymentView] = useState('subscriptions');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [currentChatMessages, setCurrentChatMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatsLoading, setChatsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const chatRef = useRef(null);
  const [chats, setChats] = useState([]);
  const [groupedChats, setGroupedChats] = useState({});
  const [isChatListStale, setIsChatListStale] = useState(false);
  const [currentChatTitle, setCurrentChatTitle] = useState(getInitialChatTitle);
  const mobileSidebarRef = useRef(null);
  const [typingState, setTypingState] = useState({
    isOtherUserTyping: false,
    typingUserColor: null,
    typingUserName: null,
    isProjectChat: false
  });
  const [activeUsers, setActiveUsers] = useState([]);
  const [projectName, setProjectName] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [headerSelectedModel, setHeaderSelectedModel] = useState(() => {
    const m = splitProviderModelKey(user?.preferences?.defaultChatModelKey);
    return m ? { provider: m.provider, id: m.model } : null;
  });
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(() => {
    try {
      return localStorage.getItem('notifications-open') === 'true';
    } catch {
      return false;
    }
  });
  const [showAccountModal, setShowAccountModal] = useState(() => {
    try {
      // Check URL param first (survives refresh, bookmarking)
      const params = new URLSearchParams(window.location.search);
      if (params.get('modal') === 'account') return true;
      // Fallback to localStorage (more reliable than sessionStorage across refresh)
      return localStorage.getItem('accountModalOpen') === '1';
    } catch {
      return false;
    }
  });
  const [artifactPanelState, setArtifactPanelState] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const { darkMode } = useTheme();

  // Viewport width tracking
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleArtifactPanelChange = useCallback((state) => {
    setArtifactPanelState(state);
  }, []);

  // Auto-collapse sidebar when both panels are open and viewport is too narrow.
  // Fires on artifact open AND on every viewport resize.
  const SIDEBAR_ARTIFACT_THRESHOLD = 1200;
  useEffect(() => {
    if (artifactPanelState?.isOpen && viewportWidth < SIDEBAR_ARTIFACT_THRESHOLD && !isSidebarCollapsed) {
      setIsSidebarCollapsed(true);
      try { localStorage.setItem('sidebarCollapsed', 'true'); } catch {}
    }
  }, [artifactPanelState?.isOpen, viewportWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const isChatRoute = location.pathname.includes('/chat') || location.pathname === '/';

  // New-chat shell: snap header + Chat to saved default (concrete `provider:model`) — no legacy auto.
  useLayoutEffect(() => {
    const p = location.pathname.replace(/\/$/, '') || '/';
    if (p !== '/chat' && p !== '/') return;
    const m = splitProviderModelKey(user?.preferences?.defaultChatModelKey);
    if (m) {
      const reset = { provider: m.provider, id: m.model };
      setHeaderSelectedModel(reset);
      chatRef.current?.setSelectedModel?.(reset);
    }
  }, [location.pathname, user?.preferences?.defaultChatModelKey]);

  useEffect(() => {
    const m = splitProviderModelKey(user?.preferences?.defaultChatModelKey);
    if (!m) return;
    setHeaderSelectedModel((prev) => prev ?? { provider: m.provider, id: m.model });
  }, [user?.preferences?.defaultChatModelKey]);

  // Clear artifact panel when navigating away from chat
  useEffect(() => {
    if (!isChatRoute) setArtifactPanelState(null);
  }, [isChatRoute]);

  // Handle return from billing portal - open Account modal with billing tab (Profile/billing is retired)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('return_from_portal') === 'true') {
      // Remove the query param immediately so we don't show Profile billing
      const cleanPath = window.location.pathname || '/';
      window.history.replaceState({}, document.title, cleanPath);
      // Open Account modal with billing tab
      localStorage.setItem('accountModalTab', 'billing');
      localStorage.setItem('accountModalOpen', '1');
      localStorage.setItem('billingReturnFromPortal', '1');
      setShowAccountModal(true);
    }
  }, []);

  // Listen for open-account-modal from Sidebar or ChatInput
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab || 'account';
      localStorage.setItem('accountModalTab', tab);
      localStorage.setItem('accountModalOpen', '1');
      setShowAccountModal(true);
      // Update URL so modal state survives refresh
      const params = new URLSearchParams(window.location.search);
      params.set('modal', 'account');
      params.set('tab', tab);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    };
    window.addEventListener('open-account-modal', handler);
    return () => window.removeEventListener('open-account-modal', handler);
  }, []);

  // Restore tab from URL on mount when modal opens from URL param
  useEffect(() => {
    if (showAccountModal) {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab) {
        try {
          localStorage.setItem('accountModalTab', tab);
        } catch {}
      }
    }
  }, [showAccountModal]);

  const openNotifications = useCallback(() => {
    setIsNotificationsOpen(true);
    try {
      localStorage.setItem('notifications-open', 'true');
    } catch (e) {
      // ignore
    }
  }, []);

  const closeNotifications = useCallback(() => {
    setIsNotificationsOpen(false);
    try {
      localStorage.removeItem('notifications-open');
    } catch (e) {
      // ignore
    }
  }, []);
  const [notificationCount, setNotificationCount] = useState(0);

  // Add groupChatsByDate function
  const groupChatsByDate = (chatsToGroup) => {
    const groups = {
      Today: [],
      Yesterday: [],
      'This Week': [],
      'Last Week': [],
      Earlier: []
    };

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    chatsToGroup.forEach(chat => {
      const chatDate = new Date(chat.lastMessageAt || chat.createdAt);
      
      if (chatDate.toDateString() === now.toDateString()) {
        groups.Today.push(chat);
      } else if (chatDate.toDateString() === yesterday.toDateString()) {
        groups.Yesterday.push(chat);
      } else if (chatDate >= thisWeekStart) {
        groups['This Week'].push(chat);
      } else if (chatDate >= lastWeekStart) {
        groups['Last Week'].push(chat);
      } else {
        groups.Earlier.push(chat);
      }
    });

    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, chats]) => chats.length > 0)
    );
  };

  const getTabFromPath = () => {
    const path = location.pathname.split('/')[1] || 'chat';
    return path;
  };

  useEffect(() => {
    setActiveTab(getTabFromPath());
  }, [location.pathname]);

  useEffect(() => {
    if (!activeChat) return;
    const cachedTitle = getCachedChatTitle(activeChat);
    if (cachedTitle && cachedTitle !== currentChatTitle) {
      setCurrentChatTitle(cachedTitle);
    }
  }, [activeChat, currentChatTitle]);

  // Update document title based on active tab
  useEffect(() => {
    const updateTitle = () => {
      switch (activeTab) {
        case 'chat':
          // For chat pages, the title will be handled by the Chat component's useDocumentTitle hook
          if (!location.pathname.includes('/chat/')) {
            document.title = 'Midus';
          }
          break;
        case 'dashboard':
          document.title = 'Dashboard - Midus';
          break;
        case 'documents':
          document.title = 'Documents - Midus';
          break;
        case 'alerts':
          document.title = 'Alerts - Midus';
          break;
        case 'profile':
          document.title = 'Profile - Midus';
          break;
        case 'pricing':
          document.title = 'Pricing - Midus';
          break;
        default:
          document.title = 'Midus';
      }
    };

    updateTitle();
  }, [activeTab, location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      const isAuthenticated = await verifyAuth();
      if (!isAuthenticated) {
        navigate('/auth/onboarding');
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Fetch notification count (pending invitations)
  const fetchNotificationCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/project-invitations/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationCount((response.data.invitations || []).length);
    } catch {
      setNotificationCount(0);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotificationCount();
  }, [fetchNotificationCount]);

  useEffect(() => {
    const handler = () => fetchNotificationCount();
    window.addEventListener('projectsUpdated', handler);
    window.addEventListener('notificationsUpdated', handler);
    return () => {
      window.removeEventListener('projectsUpdated', handler);
      window.removeEventListener('notificationsUpdated', handler);
    };
  }, [fetchNotificationCount]);

  // Remove the company data fetching useEffect since it's now handled in auth flow
  // Keep only essential initialization
  useEffect(() => {
    console.log('MainInterface: User loaded with subscription plan:', user?.company?.subscriptionPlan);
    
      // Initialize shared WebSocket service when user is available AND authenticated
  if (user?.id && isAuthenticated && !isLoading) {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔌 Initializing shared WebSocket service for user:', user.id);
      websocketService.connect(token);
    }
  }
  }, [user?.id, isAuthenticated, isLoading]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'chat' && activeChat) {
      navigate(`/chat/${activeChat}`);
    } else {
      navigate(`/${newTab}`);
    }
  };

  // Add chat list caching
  const getCachedChatList = () => {
    const cached = localStorage.getItem(CHAT_LIST_CACHE_KEY);
    if (!cached) return null;

    try {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CHAT_LIST_CACHE_DURATION) {
        return data;
      }
    } catch (e) {
      console.error('Error parsing chat list cache:', e);
    }
    return null;
  };

  const setChatListCache = (data) => {
    try {
      localStorage.setItem(CHAT_LIST_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Error setting chat list cache:', e);
    }
  };

  // Prefetch chat content
  const prefetchChat = async (chatId) => {
    if (!chatId) {
      console.warn('Attempted to prefetch chat without valid chatId');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token available for prefetching chat');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?page=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (chatRef.current?.cacheMessages && response.data) {
        chatRef.current.cacheMessages(chatId, response.data);
      }
    } catch (error) {
      console.error('Error prefetching chat:', error);
    }
  };

  // Initialize from cache on mount
  useEffect(() => {
    const cachedChats = getCachedChatList();
    if (cachedChats) {
      setChats(cachedChats);
      setGroupedChats(groupChatsByDate(cachedChats));
      setChatsLoading(false);
    } else {
      // Don't call fetchChats here - Sidebar has its own mount effect that fetches
      // This prevents duplicate GET_CHATS calls on initial load
      setChatsLoading(true);
    }
  }, []);

  useEffect(() => {
    if (sidebarRef.current) {
      const currentChats = sidebarRef.current.getChats() || [];
      const currentGroupedChats = sidebarRef.current.sortedGroupedChats || {};
      
      // Only update if the chats have actually changed or cache is stale
      if (JSON.stringify(currentChats) !== JSON.stringify(chats) || isChatListStale) {
        setChats(currentChats);
        setChatListCache(currentChats);
        setIsChatListStale(false);
        setChatsLoading(false);

        // Prefetch the first few chats with proper validation
        currentChats
          .slice(0, 3)
          .filter(chat => chat && chat._id) // Only process valid chat objects
          .forEach(chat => {
            try {
              prefetchChat(chat._id);
            } catch (error) {
              console.error('Error prefetching chat:', error);
            }
          });
      }
      if (JSON.stringify(currentGroupedChats) !== JSON.stringify(groupedChats)) {
        setGroupedChats(currentGroupedChats);
      }
    }
  }, [sidebarRef.current?.getChats(), sidebarRef.current?.sortedGroupedChats, isChatListStale, chats, groupedChats]);

  // REMOVED: Duplicate initial chat loading effect
  // The useEffect above (lines 260-275) already handles initial fetch when no cache exists
  // Having two effects both calling fetchChats() on mount caused duplicate API calls

  const handleChatSelect = async (chatId) => {
    // Set active chat and tab
    setActiveChat(chatId);
    setActiveTab('chat');
    
    if (chatId) {
      const cachedTitle = getCachedChatTitle(chatId);
      if (cachedTitle) {
        setCurrentChatTitle(cachedTitle);
      }
    }
    
    if (chatId) {
      // Store the chat ID in local storage for persistence
      localStorage.setItem('lastChatId', chatId);
      
      // Start fetching the chat content immediately
      prefetchChat(chatId);
      
      // Update sidebar to reflect the current chat
      await sidebarRef.current?.fetchChats();
      
      // Navigate to the chat URL
      navigate(`/chat/${chatId}`);

      // Prefetch the next chat in the list for better UX
      if (chats && chats.length > 0) {
        const currentIndex = chats.findIndex(chat => chat._id === chatId);
        if (currentIndex >= 0 && currentIndex < chats.length - 1) {
          prefetchChat(chats[currentIndex + 1]._id);
        }
      }
    } else {
      // If no chat ID is provided, go to the root chat page
      localStorage.removeItem('lastChatId');
      navigate('/chat');
    }
  };

  const toggleSidebar = () => {
    // If expanding the sidebar while artifact panel is open on a narrow viewport,
    // close the artifact instead to make room, then expand the sidebar.
    if (isSidebarCollapsed && artifactPanelState?.isOpen && viewportWidth < SIDEBAR_ARTIFACT_THRESHOLD) {
      artifactPanelState.onClose?.();
      setIsSidebarCollapsed(false);
      try { localStorage.setItem('sidebarCollapsed', 'false'); } catch {}
      return;
    }
    setIsSidebarCollapsed(!isSidebarCollapsed);
    try { localStorage.setItem('sidebarCollapsed', JSON.stringify(!isSidebarCollapsed)); } catch {}
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleMessagesUpdate = (messages) => {
    setCurrentChatMessages(messages);
    
    // Update typing state from chat component
    if (chatRef.current?.getTypingState) {
      const newTypingState = chatRef.current.getTypingState();
      setTypingState(newTypingState);
    }
  };

  const handleMobileMenuClick = () => {
    setIsMobileSidebarOpen(true);
  };

  const handleMobileSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleMessageSubmit = (message) => {
    // Forward the message to the Chat component through ref or state management
    if (sidebarRef.current?.handleNewMessage) {
      sidebarRef.current.handleNewMessage(message);
    }
  };

  // Handle search from header - pass it to the chat component
  const handleSearch = useCallback((searchData) => {
    if (chatRef.current?.handleSearch) {
      chatRef.current.handleSearch(searchData);
    }
  }, []);

  // Add effect to sync activeChat with URL
  useEffect(() => {
    const chatId = location.pathname.match(/\/chat\/([^/]+)/)?.[1];
    if (chatId) {
      setActiveChat(chatId);
    } else {
      // Clear activeChat when not on any chat page (including project dashboard, settings, etc.)
      setActiveChat(null);
    }
  }, [location.pathname]);

  // Update typing users from Chat component - CONVERT TO WEBSOCKET
  useEffect(() => {
    const updateTypingState = () => {
      if (chatRef.current?.typingUsers) {
        setTypingUsers(chatRef.current.typingUsers);
      } else {
        setTypingUsers(new Map());
      }
    };

    // Update immediately
    updateTypingState();

    // REMOVED: const interval = setInterval(updateTypingState, 500);
    // Now we'll rely on direct state updates from Chat component via props
    
    // Listen for typing state changes from WebSocket
    const handleTypingStateChange = () => {
      updateTypingState();
    };

    // Listen for typing events that should trigger updates
    window.addEventListener('typing-state-changed', handleTypingStateChange);

    return () => {
      window.removeEventListener('typing-state-changed', handleTypingStateChange);
    };
  }, [activeChat]); // Re-run when chat changes

  // Update activeUsers from Chat component - COPY EXACT TYPING PATTERN
  useEffect(() => {
    const updateActiveUsers = () => {
      // EXACT SAME PATTERN AS TYPING STATE - READ DIRECTLY FROM chatRef
      if (chatRef.current?.activeUsers) {
        setActiveUsers(chatRef.current.activeUsers);
      } else {
        setActiveUsers([]);
      }
    };

    // Update immediately
    updateActiveUsers();

    // SIMPLE POLLING LIKE TYPING STATE - WORKS FINE FOR READING FROM REF
    const interval = setInterval(updateActiveUsers, 1000);

    return () => clearInterval(interval);
  }, [activeChat]); // Re-run when chat changes

  // Add detection for project chat AND project dashboard
  const urlParams = new URLSearchParams(location.search);
  const projectIdFromQuery = urlParams.get('project'); // For project chats: /chat/123?project=456
  const projectIdFromPath = location.pathname.match(/^\/project\/([^/]+)/)?.[1]; // For project dashboard: /project/456
  const projectId = projectIdFromQuery || projectIdFromPath;
  const isProjectChat = !!projectIdFromQuery && (location.pathname.includes('/chat') || location.pathname === '/');
  const isProjectDashboard = !!projectIdFromPath && location.pathname.includes('/project/');

  // Add detection for client chat
  const clientIdFromQuery = urlParams.get('client'); // For client chats: /chat/123?client=456
  const clientIdFromPath = location.pathname.match(/^\/client\/([^/]+)/)?.[1]; // For client view: /client/456
  const clientId = clientIdFromQuery || clientIdFromPath;
  const isClientChat = !!clientIdFromQuery && (location.pathname.includes('/chat') || location.pathname === '/');
  const isClientView = !!clientIdFromPath && location.pathname.includes('/client/');

  // Fetch project name when in project chat or dashboard
  useEffect(() => {
    const fetchProjectName = async () => {
      console.log('🏗️ ProjectName fetch effect triggered:', { projectId, isProjectChat, isProjectDashboard });
      
      if (!projectId) {
        console.log('🏗️ No projectId, clearing project name');
        setProjectName(null);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('🏗️ No token found');
          return;
        }

        console.log('🏗️ Fetching project name for projectId:', projectId);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const fetchedProjectName = response.data.project?.name || null;
        console.log('🏗️ Fetched project name:', fetchedProjectName);
        setProjectName(fetchedProjectName);
      } catch (error) {
        console.error('🏗️ Error fetching project name:', error);
        setProjectName(null);
      }
    };

    fetchProjectName();
  }, [projectId, isProjectChat, isProjectDashboard]); // Added isProjectDashboard as dependency

  // Fetch client name when in client chat
  useEffect(() => {
    const fetchClientName = async () => {
      if (!clientId) {
        setClientName(null);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/clients/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setClientName(response.data.client?.name || null);
      } catch (error) {
        console.error('Error fetching client name:', error);
        setClientName(null);
      }
    };

    fetchClientName();
  }, [clientId, isClientChat]);

  // Function to handle project settings from header
  const handleProjectSettings = () => {
    // Dispatch a custom event that the ProjectDashboard can listen to
    window.dispatchEvent(new CustomEvent('openProjectSettings'));
  };

  // Get current chat title from chats array or fetch it
  useEffect(() => {
    const fetchChatTitle = async () => {
      if (!activeChat) {
        // Clear chat title when not in a chat so company name shows
        setCurrentChatTitle(null);
        return;
      }

      // First try to find it in the existing chats array
      const chatFromArray = chats.find(chat => chat._id === activeChat);
      if (chatFromArray?.title) {
        const resolvedTitle = chatFromArray.title !== 'New Chat' ? chatFromArray.title : 'New Chat';
        setCurrentChatTitle(resolvedTitle);
        cacheChatTitle(activeChat, resolvedTitle);
        return;
      }

      // If not found in array, fetch directly from API
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/${activeChat}/preview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const title = response.data.chat?.title;
        const resolvedTitle = title || 'New Chat';
        setCurrentChatTitle(resolvedTitle);
        cacheChatTitle(activeChat, resolvedTitle);
      } catch (error) {
        // Only log non-404/500 errors to avoid spam for invalid chat IDs
        if (!error.response || (error.response.status !== 404 && error.response.status !== 500)) {
          console.error('Error fetching chat title:', error);
        }
        // Default to "New Chat" on error - this will be handled by the Chat component's error display
        setCurrentChatTitle('New Chat');
        cacheChatTitle(activeChat, 'New Chat');
      }
    };

    fetchChatTitle();
  }, [activeChat, chats]);

  // Listen for real-time chat title updates
  useEffect(() => {
    const handleChatTitleUpdate = (event) => {
      const { chatId, title } = event.detail; // Fix: use 'title' not 'newTitle'
      console.log('🏠 MainInterface: Title update received', { chatId, title, activeChat });
      
      // Update title if it's for the current active chat
      if (chatId === activeChat) {
        console.log('🏠 MainInterface: Updating header title and triggering sidebar refresh');
        setCurrentChatTitle(title);
        cacheChatTitle(chatId, title || 'New Chat');
        
        // Also trigger sidebar refresh to update the chats list
        if (sidebarRef.current?.fetchChats) {
          sidebarRef.current.fetchChats(false, true); // Force fresh fetch to get updated title
          console.log('🏠 MainInterface: Called sidebarRef.current.fetchChats() with force=true');
        } else {
          console.warn('🏠 MainInterface: sidebarRef.current.fetchChats not available');
        }
        
        // Also refresh mobile sidebar
        if (mobileSidebarRef.current?.fetchChats) {
          mobileSidebarRef.current.fetchChats(false);
          console.log('🏠 MainInterface: Called mobileSidebarRef.current.fetchChats()');
        }
      } else {
        cacheChatTitle(chatId, title || 'New Chat');
      }
    };

    // REMOVED: handleNewChatCreated - Sidebar already handles this event
    // Having both MainInterface AND Sidebar listen for newChatCreated caused duplicate fetchChats calls

    // Listen for chat title updates only (Sidebar handles newChatCreated)
    window.addEventListener('chatTitleUpdated', handleChatTitleUpdate);

    return () => {
      window.removeEventListener('chatTitleUpdated', handleChatTitleUpdate);
    };
  }, [activeChat]);

  return (
    <TestUserProvider>
      <div className="fixed inset-0 flex h-[100dvh] overflow-hidden bg-white dark:bg-[#111111] text-black dark:text-white">
      {/* Desktop Sidebar */}
      <div className={`hidden md:block h-full transition-all duration-300 ${isSidebarCollapsed ? 'w-0 min-w-0' : ''}`}>
        <Sidebar 
          ref={sidebarRef}
          user={user} 
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          activeChat={activeChat}
          onChatSelect={handleChatSelect}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          isAccountModalOpen={showAccountModal}
        />
      </div>
      
      {/* Mobile Sidebar */}
      <MobileSidebar
        ref={mobileSidebarRef}
        isOpen={isMobileSidebarOpen}
        onClose={handleMobileSidebarClose}
        user={user}
        activeChat={activeChat}
        onChatSelect={handleChatSelect}
      />
      
      {/* Mobile Overlay */}


      {/* Floating Invitation Accepted Notifications */}
      <InvitationAcceptedNotification user={user} />

      {/* Single always-rendered layout: left col (header + chat) + right col (artifact panel) */}
      <div 
        className={`flex-1 flex min-w-0 transition-all duration-300 bg-white dark:bg-[#111111] ${
          isMobileSidebarOpen ? 'md:translate-x-0 translate-x-[280px]' : 'translate-x-0'
        }`}
      >
        {/* Left column: header + main content + mobile input. overflow-visible so the artifact panel's drag handle isn't clipped at the top. */}
        <div className="flex-1 flex flex-col min-w-[450px] overflow-visible">
          <Header 
            user={user} 
            currentChatId={activeChat}
            messages={currentChatMessages}
            onMenuClick={handleMobileMenuClick}
            onSearch={handleSearch}
            isSidebarCollapsed={isSidebarCollapsed}
            isProjectChat={isProjectChat}
            projectId={projectId}
            chatTitle={currentChatTitle}
            activeUsers={activeUsers}
            projectName={projectName}
            isProjectDashboard={isProjectDashboard}
            onProjectSettings={handleProjectSettings}
            isClientChat={isClientChat}
            clientId={clientId}
            clientName={clientName}
            selectedModel={headerSelectedModel}
            onModelSelect={(provider, model) => {
              const newModel = typeof provider === 'object' ? provider : { provider, id: model };
              setHeaderSelectedModel(newModel);
              chatRef.current?.setSelectedModel?.(newModel);
            }}
            showModelSelector={location.pathname.includes('/chat') || location.pathname === '/'}
            onNotificationsClick={openNotifications}
            onCloseNotifications={closeNotifications}
            notificationCount={notificationCount}
            isNotificationsOpen={isNotificationsOpen}
          />
          <main className="flex-1 md:overflow-hidden overflow-y-auto overscroll-none md:pb-0 pb-[max(8rem,calc(6rem+env(safe-area-inset-bottom)))]">
            {isNotificationsOpen ? (
              <NotificationsPanel
                user={user}
                isOpen={true}
                onClose={closeNotifications}
              />
            ) : (
            <MessageStatusProvider>
              <Routes>
              <Route 
                path="/chat/:chatId" 
                element={
                  <Chat 
                    ref={chatRef}
                    user={user}
                    setUser={setUser}
                    onMessagesUpdate={handleMessagesUpdate}
                    sidebarRef={sidebarRef}
                    onModelSelectionChange={setHeaderSelectedModel}
                    onArtifactPanelChange={handleArtifactPanelChange}
                    artifactPanelLifted={true}
                  />
                } 
              />
              <Route 
                path="/chat" 
                element={
                  <Chat 
                    ref={chatRef}
                    user={user}
                    setUser={setUser}
                    onMessagesUpdate={handleMessagesUpdate}
                    sidebarRef={sidebarRef}
                    onModelSelectionChange={setHeaderSelectedModel}
                    onArtifactPanelChange={handleArtifactPanelChange}
                    artifactPanelLifted={true}
                  />
                } 
              />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route 
                path="/project/:projectId" 
                element={<ProjectDashboard user={user} />} 
              />
              <Route 
                path="/client/:clientId" 
                element={<ClientView user={user} onChatSelect={handleChatSelect} />} 
              />
              <Route path="/profile/*" element={
                <Profile 
                  user={user} 
                  setIsAuthenticated={setIsAuthenticated}
                  setUser={setUser}
                  setIsOnboarded={setIsOnboarded}
                />
              } />
              <Route path="/settings/*" element={
                <Profile 
                  user={user} 
                  setIsAuthenticated={setIsAuthenticated}
                  setUser={setUser}
                  setIsOnboarded={setIsOnboarded}
                />
              } />
              <Route path="/pricing/*" element={<PricingPage refreshUser={refreshUser} />} />
              <Route 
                path="/" 
                element={
                  <Chat 
                    ref={chatRef}
                    user={user}
                    setUser={setUser}
                    onMessagesUpdate={handleMessagesUpdate}
                    sidebarRef={sidebarRef}
                    onModelSelectionChange={setHeaderSelectedModel}
                    onArtifactPanelChange={handleArtifactPanelChange}
                    artifactPanelLifted={true}
                  />
                } 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </MessageStatusProvider>
            )}
          </main>

        </div>

        {/* Right column: artifact panel — slides in/out via CSS width transition, no JSX swap */}
        {artifactPanelState?.artifact && (
          <ArtifactPanel
            isOpen={artifactPanelState?.isOpen || false}
            onOpen={() => {}}
            onClose={artifactPanelState?.onClose || (() => {})}
            artifact={artifactPanelState?.artifact}
            chatId={artifactPanelState?.chatId}
          />
        )}
      </div>
      
      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        userSubscriptions={[]}
        view={paymentView}
      />

      {/* Account Modal - opened from Sidebar profile click or ChatInput connectors */}
      <AccountModal
        isOpen={showAccountModal}
        onClose={() => {
          setShowAccountModal(false);
          try {
            localStorage.removeItem('accountModalOpen');
            localStorage.removeItem('connectToolsModalOpen');
            const params = new URLSearchParams(window.location.search);
            params.delete('modal');
            params.delete('tab');
            params.delete('connectTools');
            const newSearch = params.toString();
            window.history.replaceState({}, '', newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
          } catch {}
          window.dispatchEvent(new CustomEvent('account-modal-closed'));
        }}
        darkMode={darkMode}
        onAddConnectors={() => {}}
        user={user}
        setUser={setUser}
      />

      {/* 2FA Enforcement Modal */}
      <TwoFactorEnforcementModal 
        user={user}
        toastStyle={{
          success: { style: { background: '#10B981', color: 'white' } },
          error: { style: { background: '#EF4444', color: 'white' } }
        }}
        darkMode={false} // You may want to pass the actual dark mode state
      />
    </div>
    </TestUserProvider>
  );
};

export default MainInterface;
